import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { read, write, memoryKey } from '../utilities/pageMemory';

export const selectedHitContext = createContext(null);

// Step 31: the grant open in the right column is remembered per user by its
// "GrantWatch ID#". On return the page re-selects it from the cards it has
// loaded; if it is not among them, one request to the existing /grantLists
// route (the same body keys as the list uses, with the id in `search`)
// looks for it and the exact id match is taken; if nothing matches, the
// column stays empty, quietly. Nothing is written to any server.
const GRANT_ID = 'GrantWatch ID#';

function idOf(hit) {
  if (!hit) return null;
  const id = hit[GRANT_ID];
  return id == null || id === '' ? null : String(id);
}

export default function SelectedHitProvider({ children }) {
   const { user } = useAuth();
   const uid = (user && user.uid) || null;
   const key = memoryKey(uid, 'grant');

   let [selectedHit, setSelectedHitState] = useState(null);
   // The remembered id still waiting to be re-selected (null when none).
   const [pendingGrantId, setPendingGrantId] = useState(() => {
      const saved = read(key);
      return saved && saved.id ? String(saved.id) : null;
   });
   const readyKey = useRef(key);
   const fetchedFor = useRef(null);

   // The user's id arrives a moment after the page opens: read that user's
   // remembered grant then (and when a different user signs in).
   useEffect(() => {
      if (readyKey.current === key) return;
      readyKey.current = key;
      const saved = read(key);
      const id = saved && saved.id ? String(saved.id) : null;
      if (id && id !== idOf(selectedHit)) setPendingGrantId(id);
   }, [key, selectedHit]);

   const setSelectedHit = useCallback((hit) => {
      setSelectedHitState(hit);
      setPendingGrantId(null);
      const id = idOf(hit);
      write(readyKey.current, id ? { id, name: hit['Funding Source'] || hit['Grant Name'] || '' } : undefined);
   }, []);

   // The cards column hands over what it has loaded; if the remembered
   // grant is among them it is re-selected without any request.
   const offerGrants = useCallback((grants) => {
      if (!pendingGrantId || !Array.isArray(grants)) return false;
      const found = grants.find((g) => idOf(g) === pendingGrantId);
      if (!found) return false;
      setSelectedHitState(found);
      setPendingGrantId(null);
      return true;
   }, [pendingGrantId]);

   // Not among the loaded cards: ask the list route once for it.
   const lookUpPending = useCallback(async () => {
      const id = pendingGrantId;
      if (!id || fetchedFor.current === id) return;
      fetchedFor.current = id;
      try {
         const baseurl = process.env.REACT_APP_API_URL;
         const headers = { 'Content-Type': 'application/json' };
         if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
         const saved = read(readyKey.current) || {};
         const tries = [id];
         if (saved.name) tries.push(String(saved.name));
         for (const search of tries) {
            const res = await fetch(`${baseurl}/grantLists`, {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  pageSize: 6,
                  start: 0,
                  search,
                  categories: [],
                  agencies: [],
                  states: [],
                  deadlineAfter: '',
                  amountMin: '',
                  amountMax: '',
                  sort: '',
                  view: '',
               }),
            });
            if (!res.ok) continue;
            const data = await res.json();
            const found = (data.docs || []).find((g) => idOf(g) === id);
            if (found) {
               setSelectedHitState(found);
               setPendingGrantId(null);
               return;
            }
         }
      } catch (err) {
         // the grant is simply not re-opened
      }
      setPendingGrantId(null);
   }, [pendingGrantId, user]);

   return (
      <selectedHitContext.Provider
         value={{
            selectedHit: selectedHit,
            setSelectedHit: setSelectedHit,
            pendingGrantId: pendingGrantId,
            offerGrants: offerGrants,
            lookUpPending: lookUpPending,
         }}
      >
         {children}
      </selectedHitContext.Provider>
   );
}
